import { ref, readonly } from "vue";

export interface FollowupModalRequest {
  contactId: string;
  contactFname?: string;
  contactLname?: string;
  taskType: string;
  emailThreadId?: string;
  chatId?: string;
  // When true, immediately swaps out whatever follow-up modal is currently
  // open instead of queuing behind it (the default). Used by the power dialer
  // to close the previous contact's modal and open the next one right away.
  replace?: boolean;
}

const pendingRequest = ref<FollowupModalRequest | null>(null);

// Whether App.vue's global follow-up modal is currently on screen — kept in
// sync by App.vue (via setModalOpen) so other parts of the app (e.g. the
// power dialer banner) can react to it without owning the modal themselves.
const isModalOpen = ref(false);

// Which contact the open follow-up modal belongs to (null when closed, or when
// the incoming-call modal is the one showing). Lets the power dialer tell its
// own previous contact's modal apart from one the rep opened themselves, so it
// never swaps out a modal the rep is still filling in.
const openContactId = ref<string | null>(null);

export function useGlobalFollowupModal() {
  const openFollowupModal = (request: FollowupModalRequest) => {
    pendingRequest.value = { ...request };
  };

  const consumeRequest = (): FollowupModalRequest | null => {
    const req = pendingRequest.value;
    pendingRequest.value = null;
    return req;
  };

  const setModalOpen = (open: boolean, contactId: string | null = null) => {
    isModalOpen.value = open;
    openContactId.value = open ? contactId : null;
  };

  return {
    pendingRequest: readonly(pendingRequest),
    openFollowupModal,
    consumeRequest,
    isModalOpen: readonly(isModalOpen),
    openContactId: readonly(openContactId),
    setModalOpen,
  };
}
